<script setup>
import { timeAgo } from '@/util/time'
import { computed } from 'vue'
import { useLibraryStore } from './library.store'
import { useUserStore } from '@/authentication/user.store'
import { storeToRefs } from 'pinia'
import { getFolderOwnerUuid } from '@/library/library_helpers.ts'

const libraryStore = useLibraryStore()
const userStore = useUserStore()
const { currentFolder } = storeToRefs(libraryStore)

const props = defineProps({
  contentUuid: {
    type: String,
    required: true
  },
  folderName: {
    type: String,
    required: false
  },
  showFolderName: {
    type: Boolean,
    required: false,
    default: false
  },
  updatedTime: {
    type: String,
    required: false
  },
  url: {
    type: String,
    required: true
  },
  newWindow: {
    type: Boolean,
    default: false
  },
  bgColor: {
    type: String,
    default: 'bg-surface-50'
  },
  clickable: {
    type: Boolean,
    default: true
  },
  lockedMessage: {
    type: String,
    required: false,
    default: ''
  },
  hideFooterBand: {
    type: Boolean,
    default: false
  },
  noPaddingBottom: {
    type: Boolean,
    default: false
  }
})

const updatedTimeAgo = computed(() => {
  return timeAgo(props.updatedTime)
})

const linkEnabled = computed(() => {
  return props.clickable && !props.lockedMessage
})

const isOwner = computed(() => {
  return getFolderOwnerUuid(currentFolder.value) === userStore.userId
})

const isContentNew = computed(() => {
  if (!props.contentUuid || !currentFolder.value?.uuid || !isOwner.value) {
    return false
  }
  return libraryStore.isContentNew(props.contentUuid, currentFolder.value.uuid)
})
</script>

<template>
  <a
    :href="linkEnabled ? props.url : undefined"
    :target="linkEnabled && props.newWindow ? '_blank' : '_self'"
    class="flex h-full focus-visible:outline-primary-500"
    :tabindex="!linkEnabled ? '-1' : undefined"
    :aria-disabled="!linkEnabled ? 'true' : undefined"
    :title="!props.clickable ? 'This content is not available' : undefined"
  >
    <div
      class="library-card group relative flex grow flex-col rounded-xs shadow-lib-card"
      :class="[
        bgColor,
        linkEnabled && 'hover:shadow-lib-card-hover',
        !linkEnabled && 'cursor-not-allowed'
      ]">
      <p
        v-if="lockedMessage"
        class="absolute inset-0 flex items-center justify-center bg-black bg-opacity-75 text-white rounded-xs opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 px-3 py-2 text-center cursor-not-allowed">
        {{ lockedMessage }}
      </p>
      <div class="card-body relative h-full px-5 pt-5" :class="noPaddingBottom ? 'pb-0' : 'pb-3'">
        <div v-if="isContentNew" class="w-2 h-2 bg-blue-500 rounded-full absolute top-2 left-2"></div>
        <slot />
      </div>
      <div class="card-footer rounded-b-xs px-5 py-3" :class="hideFooterBand ? bgColor : 'bg-gray-200'">
        <div class="flex h-4 justify-between">
          <div v-if="updatedTime && !showFolderName" class="flex items-center" :class="hideFooterBand ? 'text-weak' : ''">
            <span class="material-icons-round text-lg">calendar_month</span>
            <span class="pl-1 text-sm">Updated {{ updatedTimeAgo }}</span>
          </div>
          <div v-else-if="folderName && showFolderName" class="flex items-center" :class="hideFooterBand ? 'text-weak' : ''">
            <span class="material-icons-round text-lg">folder</span>
            <span class="pl-1 text-sm">{{ folderName }}</span>
          </div>
          <div class="card-footer-right flex items-center p-1">
            <slot name="footer-right" />
          </div>
        </div>
      </div>
    </div>
  </a>
</template>
